import { getGradeForStudyProgramme } from './getGradeForStudyProgramme';
import { CONTENTAPI_DOCUMENT_KEYS } from '@store/contentApi/contentApiDataAccess';
import {
  selectCurriculaWithMultipleActiveVersions,
  selectEducationalComponents,
  selectEducationalPointers,
} from '@store/contentApi/contentApiSelectors';
import { selectLeidraadTop5AndEduPointers } from '@store/leidraad/leidraadSelectors';
import {
  selectCustomCurriculaData,
  selectCustomItemsInFormat,
} from '@store/llinkidApis/llinkidApiSelectors';
import {
  selectAllCreatorsActiveInSchoolyear,
  selectAllCreatorsForSchoolThatEverExisted,
  selectBossOrgHrefs,
  selectCurrentOrg,
  selectCurrentSchoolHref,
  selectCurrentSchoolyear,
  selectIsSchoolyearReadOnly,
  selectLoggedInUser,
  selectSchoolyearFromParamOrCurrent,
} from '@store/userAndSchool/userAndSchoolSelectors';
import {
  CONTENTTYPES,
  CUSTOMCURRICULATYPES,
  CUSTOMTYPES,
  TABNAMES,
  excludeMandatoryResources,
  flatRefrencedCurriculaItems,
  getCustomGoalList,
  modifyGoalsForDistribution,
  setCollapsedSections,
} from '@utils/curriculumHelper';
import { flattenAndExtractCurriculaItems } from '@utils/flattenAndExtractCurriculaItems';
import { getKeyFromHref } from '@utils/getKeyFromHref';
import {
  conditionalLogTime,
  deepFreeze,
  formatVersionNumber,
  getViewModelEntities,
  validityPeriodToString,
} from '@utils/utils';
import { isEqual, last } from 'lodash-es';
// import { diffString } from 'json-diff';

import { selectSchoolStudyProgrammes } from '@store/leerplanList/leerplanListSelectors';
import { logAndCaptureException } from '@utils/logAndCaptureException';
import { getStreamsFinalitiesOfStudyPrograms } from '@store/leerplanList/leerplanListHelpers';
import CurriculumNode from '../../module/models/curriculumNode';
import {
  addDraggable,
  buildAnnotations,
  buildGoalListItems,
  buildGoalListTab,
  buildGoalsFromExtractedCurriculumNodes,
  buildHeaderInfo,
  buildIntroductionTab,
  buildRichTextTab,
  getCurriculumKeyFromStateOrParams,
  getCustomCurriculaFromIds,
  getCustomCurriculaFromSetId,
  getCustomCurriculasInOrder,
  getGoalsToLoad,
  getItemsFromReferences,
  getLayeredAnnotationsObj,
  getTitleObjects,
  triggerSelectedItems,
} from './curriculumAndAnnotationHelper';

import type { RootState } from '@store/storeSetup';
import type { ApiStudyProgrammeGroup } from '../../types/samApiTypes';
import { createTypedSelector } from '@store/genericHelpers';
import type { CurriculumParams } from './CurriculumParams';
import type { LVLlinkidCurriculumAndChild, LVLlinkidGoalList } from '../../types/contentApiTypes';
import type { ApiCustomCurriculum } from '../../types/llinkidApiTypes';
import type { UniqueGoalIdParams } from './curriculumTypes';

const emptyArray = [];

export const selectCustomCurriculumFromKey = (
  state: RootState,
  key: string,
): ApiCustomCurriculum | undefined => {
  const customCurricula = selectCustomCurriculaData(state);
  return customCurricula.find((e) => e.key === key);
};

export const selectIsSelectionReadonlyMode = (state: RootState): boolean =>
  state.curriculum.selectionOptions.readonlyMode;

export const selectCustomCurriculaFromUrlOrParams = createTypedSelector(
  [
    (state, params?: CurriculumParams) => (params ? params.setid : state.curriculum.setid),
    (state, params?: CurriculumParams) => (params ? params.studids : state.curriculum.studids),
    (state, params?: CurriculumParams) => (params ? params.custids : state.curriculum.custids),
    selectCustomCurriculaData,
  ],
  (setid, studids, custids, allCustomCurriculas) => {
    return getCustomCurriculaFromIds(
      setid,
      studids,
      custids,
      allCustomCurriculas,
    ) as ApiCustomCurriculum[];
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectIsBaseCurriculum = (state: RootState): boolean =>
  !selectCustomCurriculaFromUrlOrParams(state);

export const selectCustomGoalIdentifiersSet = createTypedSelector(
  [(state) => state.customCurriculaData.customItems],
  (customItems) =>
    new Set(
      Object.values(customItems)
        .map((item) => item.identifier)
        .filter(Boolean),
    ),
);

export const selectUniqueGoalId = (state: RootState, params: UniqueGoalIdParams): string => {
  let idNumber = 1;
  let newId = `${params.curriculumId} ${idNumber}`;
  const identifiersSet = selectCustomGoalIdentifiersSet(state);

  while (identifiersSet.has(newId)) {
    idNumber += 1;
    newId = `${params.curriculumId} ${idNumber}`;
  }

  return newId;
};

/**
 * this selector returns ALL of the customcurricula that match the setid.
 * the difference with the selector above is that this one returns all the customcurricula, and ignores the current studids.
 * the use case is for the distribute modal where we need to filter studyprogrammes that are not available for this set
 */
const selectCustomCurriculaFromSetid = createTypedSelector(
  [
    (state, params?: CurriculumParams) => (params ? params.setid : state.curriculum.setid),
    selectCustomCurriculaData,
  ],
  (setid, allCustomCurriculas) => {
    if (!setid) return emptyArray;
    return getCustomCurriculaFromSetId(setid, allCustomCurriculas) as ApiCustomCurriculum[];
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

const selectNonDerivedTopLevelCurricula = createTypedSelector(
  [
    (state, params?: CurriculumParams) => (params ? params.setid : state.curriculum.setid),
    selectCustomCurriculaFromSetid,
    selectCustomCurriculaFromUrlOrParams,
    (state) => state.customCurriculaData.customCurricula,
  ],
  (setid, customCurriculaFromSetId, customCurriculaFromUrlOrParams, allCustomCurriculasMap) => {
    // in the case of the smartschool curriculum selector, there is no setId when opening a non-derived.
    // so we fall back to getting the customcurricula from the url via custids.
    const customCurriculaFromUrl = setid
      ? customCurriculaFromSetId
      : customCurriculaFromUrlOrParams;
    if (!customCurriculaFromUrl) return emptyArray;

    if (customCurriculaFromUrl.every((z) => z.source === null)) {
      return customCurriculaFromUrl;
    }
    if (
      customCurriculaFromUrl[0].type === CUSTOMCURRICULATYPES.adaptation &&
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      !customCurriculaFromUrl[0].source!.href.startsWith('/content')
    ) {
      return customCurriculaFromUrl.map(
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        (item) => allCustomCurriculasMap[last(item.source!.href.split('/')) as string],
      );
    }
    logAndCaptureException('not a nonderived');
    return undefined;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectCurriculumKey = (state: RootState) => state.curriculum.curriculumKey;

export const selectCurriculumStudyProgrammeHrefs = createTypedSelector(
  [
    (state, params?: CurriculumParams) =>
      getCurriculumKeyFromStateOrParams(state, params) === 'nonderived'
        ? selectNonDerivedTopLevelCurricula(state, params)
        : undefined,
    (state, params?: CurriculumParams) =>
      state.contentApiData.curricula[getCurriculumKeyFromStateOrParams(state, params)],
    (state) => state.contentApiData.curriculaRoots,
    (state, params?: CurriculumParams) => getCurriculumKeyFromStateOrParams(state, params),
  ],
  (NDTopLvlCur, baseCurricula, allBaseCurriculaFromList, curriculumKey) => {
    /**
     * the baseCurriculum comes from an api call /content/guid/llinkidCurricula
     * that data is possibly undefined if that call is still loading.
     * we do have all root nodes for the list screen in curriculaRoots, and the root contains the studyprogrammes, so we use that as a fallback.
     */
    let curriulumStudyProgrammes: Array<{ href: string }> = [];
    if (NDTopLvlCur) {
      curriulumStudyProgrammes = NDTopLvlCur.flatMap((z) => z.applicability.studyProgrammes);
    } else {
      const definedBaseCurricula =
        baseCurricula || allBaseCurriculaFromList.find((e) => e.key === curriculumKey);
      curriulumStudyProgrammes = definedBaseCurricula?.applicability?.studyProgrammes || [];
    }
    return curriulumStudyProgrammes;
  },
);

export const selectCurriculumStudyProgrammes = createTypedSelector(
  [selectCurriculumStudyProgrammeHrefs, (state) => state.studyProgrammeApiData.allPrograms],
  (curriculumStudyProgrammes, allPrograms) => {
    if (!curriculumStudyProgrammes || !allPrograms) return emptyArray;
    return curriculumStudyProgrammes
      .map((z) => allPrograms.find((e) => e.$$meta.permalink === z.href))
      .filter(Boolean); // filter out packet etc.
  },
);

export const selectDoCustomCurriculumStudyProgrammesMatchSchoolStudyProgrammes =
  createTypedSelector(
    [selectCurriculumStudyProgrammeHrefs, selectSchoolStudyProgrammes],
    (curriculumStudyProgrammes, schoolStudyProgrammes) =>
      curriculumStudyProgrammes?.some((curriculumStudyProgram) =>
        schoolStudyProgrammes?.some(
          (schoolStudyProgram) =>
            schoolStudyProgram.$$meta.permalink === curriculumStudyProgram.href,
        ),
      ),
  );

export const selectCustomCurriculaStudyProgrammeHrefs = createTypedSelector(
  [selectCustomCurriculaFromSetid],
  (customCurricula) => {
    if (!customCurricula) return emptyArray;
    return customCurricula.flatMap((z) => z.applicability.studyProgrammes).map((z) => z.href);
  },
);

export const selectCurriculumGrade = (
  state: RootState,
  params?: CurriculumParams,
): ApiStudyProgrammeGroup | undefined => {
  const curriculumStudyProgrammes = selectCurriculumStudyProgrammeHrefs(state, params);
  if (!curriculumStudyProgrammes || curriculumStudyProgrammes.length === 0) return undefined;
  const { allPrograms, allGrades } = state.studyProgrammeApiData;
  if (!allPrograms?.length) return undefined;

  const [studyProgram] = curriculumStudyProgrammes;
  const fullStudyProgram = allPrograms.find((e) => e.$$meta.permalink === studyProgram.href);
  return getGradeForStudyProgramme(fullStudyProgram, allGrades);
};

export const selectCurriculumFinality = (state: RootState, params?: CurriculumParams) => {
  const curriculumStudyProgrammes = selectCurriculumStudyProgrammes(state, params);
  if (!curriculumStudyProgrammes || curriculumStudyProgrammes.length === 0) return undefined;
  const { allPrograms } = state.studyProgrammeApiData;
  if (!allPrograms?.length) return undefined;

  const { firstGradeStreams, secondGradeFinalities } = state.studyProgrammeApiData;

  const finalities = getStreamsFinalitiesOfStudyPrograms(
    firstGradeStreams,
    secondGradeFinalities,
    curriculumStudyProgrammes,
  );

  return finalities;
};

const seventhYearFinalities = [
  '/sam/commons/studyprogrammegroups/2a498a7c-fa7c-4b0c-8658-08201ed67d0b',
  '/sam/commons/studyprogrammegroups/d350d5a0-7eea-493e-beb6-60f5b654e21c',
  '/sam/commons/studyprogrammegroups/e8a6d467-5f94-4b7f-add5-7e7b8ce79b15',
];

export const selectIsCurriculum7thYear = (state: RootState, params?: CurriculumParams) => {
  const finality = selectCurriculumFinality(state, params);
  if (!finality) return false;
  return seventhYearFinalities.some((sev) => finality.includes(sev));
};

export const selectGoalList = (state: RootState) => {
  const originalCurriculum =
    state.contentApiData.curricula[getCurriculumKeyFromStateOrParams(state)];
  if (!originalCurriculum) return undefined;
  const goalList = originalCurriculum.children.find((e) => e.type === CONTENTTYPES.goalList);
  return goalList;
};

const selectExtractedCurriculum = createTypedSelector(
  [
    (state, params?: CurriculumParams) =>
      state.contentApiData.curricula[getCurriculumKeyFromStateOrParams(state, params)],
  ],
  (originalCurriculum) => {
    if (!originalCurriculum) return undefined;
    const goalList = originalCurriculum.children.find(
      (e) => e.type === CONTENTTYPES.goalList,
    ) as LVLlinkidGoalList;
    const flats = flattenAndExtractCurriculaItems(goalList);
    return flats;
  },
);

export const selectCurriculumGradeForIntroduction = (
  state: RootState,
  params?: CurriculumParams,
) => {
  const grade = selectCurriculumGrade(state, params);
  const is7thYear = selectIsCurriculum7thYear(state, params);
  return is7thYear ? '7' : grade?.title;
};

export const selectBaseCurriculumViewModel = createTypedSelector(
  [
    (state, params?: CurriculumParams) => getCurriculumKeyFromStateOrParams(state, params),
    (state, params?: CurriculumParams) =>
      state.contentApiData.curricula[getCurriculumKeyFromStateOrParams(state, params)],
    selectEducationalComponents,
    selectEducationalPointers,
    (state, params?: CurriculumParams) => {
      const gradeKey = selectCurriculumGradeForIntroduction(state, params);
      if (gradeKey) {
        return state.contentApiData.documents[
          CONTENTAPI_DOCUMENT_KEYS.INTRODUCTION_GRADE[gradeKey]
        ];
      }
      return undefined;
    },
    selectCurriculumGrade,
    (state) =>
      selectCurriculaWithMultipleActiveVersions(state, {
        schoolyearKey: state.userAndSchools.currentSchoolyear,
      }),
  ],
  (
    curriculumKey,
    originalCurriculum,
    edComponents,
    edPointers,
    introduction,
    grade,
    curriculaMultipleActiveVersionsMap,
  ) => {
    if (!originalCurriculum || !curriculumKey || curriculumKey === 'nonderived') return undefined;
    const endLogTime = conditionalLogTime('selectBaseCurriculumViewModel', 2);
    const curriculum = { ...originalCurriculum };

    const versionNumber = formatVersionNumber({
      version: curriculum.version,
      grade,
      versionStatus: curriculaMultipleActiveVersionsMap?.[curriculum.key],
    });

    const validityPeriodString = validityPeriodToString(curriculum.validityPeriod);
    const studyPrograms = curriculum.applicability?.studyProgrammes.map((i) =>
      last(i.href.split('/')),
    );

    let viewModel = {
      inleiding: {},
      situering: {},
      duiding: {},
      basisuitrusting: {},
      concordantielijst: {},
      generalLevel: {},
      educationalPointers: [],
      educationalComponents: [],
      curriculumType: 'PLAN',
      subTitle: '',
      studyProgram: '',
      readonly: true,
      grade,
      ...curriculum,
      children: undefined,
      versionNumber,
      validityPeriodString,
      studyPrograms,
    };

    viewModel.inleiding = introduction && buildIntroductionTab(introduction);
    // the idea for inleiding is, that it will have its own VM, since it's the same for every curriculum of the same grade.
    // only load the inleiding when we need it visualized.
    viewModel.situering = buildRichTextTab(curriculum, 'situering');
    viewModel.duiding = buildRichTextTab(curriculum, 'duiding');
    viewModel.basisuitrusting = buildRichTextTab(curriculum, 'basisuitrusting');
    viewModel.concordantielijst = buildRichTextTab(curriculum, 'concordantielijst');

    const principleGroup = curriculum.children.find(
      (res) => res.type === CONTENTTYPES.principleGroup,
    );
    viewModel.generalLevel = principleGroup || {};

    viewModel = buildHeaderInfo(viewModel, edPointers, edComponents);

    endLogTime();
    return viewModel;
  },
);

export const selectCreatorFromCustomCurricula = createTypedSelector(
  [
    (state) => selectCustomCurriculaFromUrlOrParams(state),
    (state) => selectAllCreatorsForSchoolThatEverExisted(state),
  ],
  (customCurFromUrl, currentOrgs) => {
    if (customCurFromUrl?.length) {
      return currentOrgs.find((org) => org.$$meta.permalink === customCurFromUrl[0].creator.href);
    }
    return null;
  },
);

export const selectCreator = (state: RootState) => {
  return selectCreatorFromCustomCurricula(state) || selectCurrentOrg(state);
  // current org depends.
  // if we are in a customcur, the current org is the creator of the customcur.
  // if we are in a base curricula, the current org is whatever is in the state. (distribution screen)
};

export const selectCreatorFromCurriculumNiveau = (state: RootState, curriculum: any) => {
  const user = selectLoggedInUser(state);
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const currentOrg = selectCurrentOrg(state)!;

  switch (curriculum.niveau) {
    case 'school':
      return currentOrg.$$meta.permalink;
    case 'group':
      return curriculum.group;
    case 'persoonlijk':
      return user.$$meta.permalink;
    default:
      return currentOrg.$$meta.permalink;
  }
};

export const selectCurriculumAttachments = (state: RootState, params?: CurriculumParams) =>
  state.contentApiData.curricula[params?.curriculumKey || state.curriculum.curriculumKey]
    ?.attachments || emptyArray;

export const selectIsCurriculumFoundational = (state: RootState) =>
  state.contentApiData.curricula[state.curriculum.curriculumKey]?.foundational;

export const selectSelectedStudyPrograms = createTypedSelector(
  [
    selectCustomCurriculaFromUrlOrParams,
    (state) => state.curriculum.studids,
    (state) => state.studyProgrammeApiData.allPrograms,
  ],
  (customCurFromUrl, studids, allPrograms) => {
    if (allPrograms && (studids?.length || customCurFromUrl?.length)) {
      let spKeys = studids;
      if (customCurFromUrl) {
        // in case of a customcur, we look at the customcur. in case of a foundational/distribution we look at the studids because no customcur exist.
        spKeys = customCurFromUrl.map((e) =>
          getKeyFromHref(e.applicability.studyProgrammes[0].href),
        );
      }
      return allPrograms.filter((e) => spKeys.includes(e.key));
    }
    return emptyArray;
  },
);

export const selectBaseNonDerivedCurriculumViewModel = createTypedSelector(
  [
    (state, params?: CurriculumParams) => getCurriculumKeyFromStateOrParams(state, params),
    selectCustomCurriculaFromUrlOrParams,
    selectCurriculumGrade,
    selectNonDerivedTopLevelCurricula,
  ],
  (
    curriculumKey,
    customCurricula,
    grade,
    // curriculaMultipleActiveVersionsMap,
    nonderivedTopLevelCurricula,
  ) => {
    if (
      !nonderivedTopLevelCurricula ||
      nonderivedTopLevelCurricula.length === 0 ||
      curriculumKey !== 'nonderived'
    )
      return undefined;
    const endLogTime = conditionalLogTime('selectBaseNonDerivedCurriculumViewModel', 2);

    const item = nonderivedTopLevelCurricula[0];

    const studyPrograms = customCurricula.map((e) =>
      getKeyFromHref(e.applicability.studyProgrammes[0].href),
    );

    const viewModel = {
      title: item.title,
      inleiding: {},
      situering: {},
      duiding: {},
      basisuitrusting: {},
      concordantielijst: {},
      generalLevel: {},
      educationalPointers: [],
      educationalComponents: [],
      // curriculumType: 'PLAN',
      subTitle: '',
      studyProgram: '',
      studyPrograms,
      readonly: true,
      grade,
      identifiers: [item.identifier],
      doelenlijst: getCustomGoalList(),
      nonderivedGroupKey: item.customCurriculaGroup
        ? item.customCurriculaGroup.href
        : item.$$meta.permalink,
      version: formatVersionNumber({ version: item.$$version }), // emulate API
      versionNumber: formatVersionNumber({ version: item.$$version }), // emulate baseCurVM
      validityPeriodString: validityPeriodToString(customCurricula[0].issued),
    };

    endLogTime();
    return viewModel;
    // return viewModel;
  },
);

const selectAllGoalFromCurricula = createTypedSelector(
  [(state) => state.contentApiData.curricula],
  (curricula) => {
    const goals = {};
    Object.values(curricula).forEach((curr) => {
      flatRefrencedCurriculaItems(curr)
        .filter((e) => e.type === CONTENTTYPES.goal || e.type === CUSTOMTYPES.goal)
        .forEach((goal) => {
          goals[goal.key] = goal;
        });
    });
    return goals;
  },
);

const selectAllGoalFromGoals = createTypedSelector(
  [(state) => state.contentApiData.goals],
  (goalsWithChildren) => {
    const goals = {};
    Object.values(goalsWithChildren).forEach((curr) => {
      flatRefrencedCurriculaItems(curr)
        .filter((e) => e.type === CONTENTTYPES.goal)
        .forEach((goal) => {
          goals[goal.key] = goal;
        });
    });
    return goals;
  },
);

const selectAllCustomGoals = createTypedSelector([selectCustomItemsInFormat], (customItems) => {
  const goals: Record<string, any> = {};
  Object.values(customItems as any).forEach((curr) => {
    const currr = curr as any;
    if (currr.type === CUSTOMTYPES.goal) {
      goals[currr.key] = curr;
    }
  });
  return goals;
});

export const selectAllGoals = createTypedSelector(
  [selectAllGoalFromCurricula, selectAllGoalFromGoals, selectAllCustomGoals],
  (allGoalsFromCur, extraGoals, customGoals) => {
    return { ...allGoalsFromCur, ...extraGoals, ...customGoals };
  },
);

export const selectBuiltGoals = (state: RootState, goalHrefs: string[]) => {
  const edPointers = selectEducationalPointers(state);
  const { educationalActivityTypes } = state.studyProgrammeApiData;
  const allGoals = selectAllGoals(state);

  const goals = goalHrefs.map((href) => allGoals[getKeyFromHref(href)]).filter((e) => e);

  if (!goals.length) return goals;

  const builtGoals = buildGoalsFromExtractedCurriculumNodes(
    goals,
    edPointers,
    educationalActivityTypes,
  );

  return builtGoals;
};

/**
 *
 * @param {*} state
 * @returns an object with which tryCreateCustomCurricula can be called in the dataservice, or false.
 */
export const selectAreCustomCurriculaMissing = (state: RootState) => {
  const customCurricula = selectCustomCurriculaFromUrlOrParams(state);
  const { studids } = state.curriculum;
  const { allPrograms } = state.studyProgrammeApiData;
  const schoolyear = selectCurrentSchoolyear(state);

  if (!customCurricula || !customCurricula[0]) return false;

  const curriculasSPKeys = customCurricula.map((e) =>
    getKeyFromHref(e.applicability.studyProgrammes[0].href),
  );

  if (studids?.length && !isEqual([...studids].sort(), curriculasSPKeys.sort())) {
    // studids might be undefined, coming from smartschool
    const curriculum = customCurricula[0];
    if (curriculum.source?.href.startsWith('/content')) {
      const hrefs = allPrograms
        .filter((s) => studids.includes(s.key))
        .map((e) => e.$$meta.permalink);
      return {
        curriculum: {
          curriculumType: curriculum.type,
          sources: hrefs.map((s) => ({
            studyProgram: s,
            href: curriculum.source?.href,
          })),
        },
        creator: curriculum.creator.href,
        studyPrograms: hrefs,
        schoolyear,
      };
    }
  }
  return false;
};

/**
 * returns all goal hrefs that are referenced in the curriculum.
 * it does not return hrefs to custom goals.
 */
const selectReferencedGoalHrefsFromCurriculum = createTypedSelector(
  [
    (state, params?: CurriculumParams) =>
      params
        ? selectCustomCurriculaFromUrlOrParams(state, params)
        : selectCustomCurriculaFromUrlOrParams(state),
    (state, params?: CurriculumParams) => selectAllCreatorsActiveInSchoolyear(state, params),
    (state, params?: CurriculumParams) => selectSchoolyearFromParamOrCurrent(state, params),
    (state) => state.customCurriculaData.annotations,
    (state) => state.customCurriculaData.customCurricula,
  ],
  (customCurricula, orgs, currentSchoolyear, allAnnotationsMap, allCustomCurriculasMap) => {
    if (!customCurricula) return emptyArray;

    const customCurriculasInOrderObj = getCustomCurriculasInOrder(
      customCurricula,
      orgs,
      currentSchoolyear,
      allCustomCurriculasMap,
    );

    const customCurHrefs: string[] = [];
    Object.keys(customCurriculasInOrderObj).forEach((key) => {
      const custCurInOrder = customCurriculasInOrderObj[key];
      custCurInOrder.forEach((cc) => customCurHrefs.push(cc.$$meta.permalink));
    });

    const annotations = Object.values(allAnnotationsMap).filter(
      (annotation) =>
        customCurHrefs.includes(annotation.curriculum.href) &&
        annotation.type === CUSTOMTYPES.goalReference, // only references would need translation
    );

    const goalsFoundInCur = new Set();

    annotations.forEach((annotation) => {
      if (annotation.target?.href.startsWith('/content')) {
        goalsFoundInCur.add(annotation.target.href);
      }
    });

    return [...goalsFoundInCur];
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

// find missingGoals
export const selectExtraGoalsToLoad = createTypedSelector(
  [
    selectReferencedGoalHrefsFromCurriculum,
    (state) => state.curriculum.preview,
    selectAllGoals,
    (state) => state.contentApiData.goalsToLoad,
  ],

  (goalsToLoadHrefs, preview, allGoals, goalsToLoadStatuses) => {
    return getGoalsToLoad(goalsToLoadHrefs, allGoals, goalsToLoadStatuses, preview);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectIsCurriculumLoaded = createTypedSelector(
  [
    (state, params?: CurriculumParams) => params?.curriculumKey || state.curriculum.curriculumKey,
    selectReferencedGoalHrefsFromCurriculum,
    (state, params?: CurriculumParams) =>
      state.contentApiData.curricula[params?.curriculumKey || state.curriculum.curriculumKey],
    (state, params?: CurriculumParams) =>
      state.contentApiData.curriculaToLoad[params?.curriculumKey || state.curriculum.curriculumKey],
    selectAllGoals,
    (state) => state.contentApiData.goalsToLoad,
  ],
  (curriculumKey, referencedGoalHrefs, curricula, curriculaToLoad, allGoals, goalsToLoad) => {
    const isBaseCurrLoaded =
      curriculumKey === 'nonderived' || !!curricula || curriculaToLoad?.isFailed === true;

    const isLoaded =
      isBaseCurrLoaded &&
      referencedGoalHrefs.every((e) => {
        const key = getKeyFromHref(e);
        return allGoals[key] || goalsToLoad[key]?.isFailed;
      });
    return isLoaded;
  },
  {
    memoizeOptions: {},
  },
);

export const selectLayeredAnnotationsObjFromUrlOrParams = createTypedSelector(
  [
    selectCustomCurriculaFromUrlOrParams,
    (state, params?: CurriculumParams) => selectSchoolyearFromParamOrCurrent(state, params),
    (state, params?: CurriculumParams) => selectAllCreatorsActiveInSchoolyear(state, params),
    (state) => state.customCurriculaData.annotations,
    (state) => selectCustomItemsInFormat(state),
    (state) => state.customCurriculaData.customCurricula,
    selectAllGoals,
  ],
  (
    customCurricula,
    currentSchoolyear,
    orgs,
    annotationsMap,
    customitemsMap,
    customCurriculaMap,
    goalsMap,
  ) => {
    const layeredAnnotationsObj = getLayeredAnnotationsObj({
      customCurricula,
      currentSchoolyear,
      orgs,
      annotationsMap,
      customCurriculaMap,
    });
    const referencedItems =
      layeredAnnotationsObj &&
      getItemsFromReferences(layeredAnnotationsObj, customitemsMap, goalsMap);
    return deepFreeze({ layeredAnnotationsObj, referencedItems });
  },
  {
    memoizeOptions: {
      // resultEqualityCheck: deepEqualWithLog('selectLayeredAnnotationsObjFromUrlOrParams'),
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectAnnotatedCurriculumGoalListData = createTypedSelector(
  [
    (state, params?: CurriculumParams) => getCurriculumKeyFromStateOrParams(state, params),
    selectEducationalPointers,
    (state) => state.studyProgrammeApiData.educationalActivityTypes,
    selectExtractedCurriculum,
    selectLayeredAnnotationsObjFromUrlOrParams,
    (state) => state.studyProgrammeApiData.allPrograms,
  ],
  (
    curriculumKey,
    edPointers,
    educationalActivityTypes,
    extractedCurriculum,
    layeredAnnotationsObjResult,
    allPrograms,
  ) => {
    const endLogTime = conditionalLogTime('selectAnnotatedCurriculumGoalListData', 2);
    const { layeredAnnotationsObj, referencedItems } = layeredAnnotationsObjResult;
    let extractedCurriculumNodes = extractedCurriculum;

    if (curriculumKey === 'nonderived') {
      extractedCurriculumNodes = flattenAndExtractCurriculaItems(
        getCustomGoalList() as unknown as LVLlinkidGoalList,
      );
    }

    if (extractedCurriculumNodes) {
      if (referencedItems) {
        Object.values(referencedItems).forEach((item) => {
          const itemWithDraggable = addDraggable(item);
          const extract = flattenAndExtractCurriculaItems(itemWithDraggable);
          extractedCurriculumNodes = {
            ...extractedCurriculumNodes,
            ...extract,
          };
        });
      }

      extractedCurriculumNodes = excludeMandatoryResources(
        buildGoalListItems(extractedCurriculumNodes, edPointers, educationalActivityTypes),
      );

      if (layeredAnnotationsObj) {
        extractedCurriculumNodes = buildAnnotations(
          layeredAnnotationsObj,
          extractedCurriculumNodes,
          allPrograms,
        );
      }
    }

    endLogTime();
    return extractedCurriculumNodes;
  },
  {
    memoizeOptions: {
      // equalityCheck: (previousVal, currentVal) => {
      //   const rv = currentVal === previousVal;
      //   if (!rv) console.log('Selector param value changed', currentVal);
      //   return rv;
      // },
      resultEqualityCheck: isEqual,
      // resultEqualityCheck: deepEqualWithLog('selectAnnotatedCurriculumGoalListData'),
    },
  },
);

export const selectCurriculumGoalHrefs = createTypedSelector(
  [selectAnnotatedCurriculumGoalListData],
  (extractedCurriculumNodes) => {
    if (!extractedCurriculumNodes) return extractedCurriculumNodes;
    return Object.values(extractedCurriculumNodes as LVLlinkidCurriculumAndChild[])
      .filter((z) => z.type === CONTENTTYPES.goal || z.type === CUSTOMTYPES.goal)
      .map((e) => e.href);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

const selectGoalListItems = createTypedSelector(
  [selectAnnotatedCurriculumGoalListData],
  (extractedCurriculumNodes): any[] | undefined => {
    if (!extractedCurriculumNodes) return undefined;
    const endLogTime = conditionalLogTime('selectAnnotatedGoalFromCurriculumViewModel', 2);
    const goalList = extractedCurriculumNodes && buildGoalListTab(extractedCurriculumNodes);
    const items = getViewModelEntities(goalList);
    endLogTime();

    return items;
  },
);

export const selectGoalsFromCurriculum = createTypedSelector([selectGoalListItems], (goalList) => {
  const goals = goalList?.filter(
    (goal) => goal.type === CONTENTTYPES.goal || goal.type === CUSTOMTYPES.goal,
  );
  return goals;
});

export const selectGoalsAndSectionAndGoalListFromCurriculum = createTypedSelector(
  [(state, params?: CurriculumParams) => selectGoalListItems(state, params)],
  (goalList) => {
    const goals = goalList?.filter(
      (item) =>
        item.type === CONTENTTYPES.goal ||
        item.type === CUSTOMTYPES.goal ||
        item.type === CONTENTTYPES.goalSection ||
        item.type === CUSTOMTYPES.section ||
        item.type === CUSTOMTYPES.goalList ||
        item.type === CONTENTTYPES.goalList,
    );
    return goals;
  },
);

export const selectCanEditCustomCur = createTypedSelector(
  [selectBossOrgHrefs, selectCustomCurriculaFromUrlOrParams],
  (bossOrgHrefs, customCurriculaFromUrl) => {
    if (!customCurriculaFromUrl || !customCurriculaFromUrl.length) return false;
    return bossOrgHrefs.includes(customCurriculaFromUrl[0].creator.href);
  },
);

export const selectHideOptionalElements = createTypedSelector(
  [
    (state) => state.userAndSchools.privateState?.state?.hideOptionalElements,
    (state) => state.curriculum.setid,
    (state) => state.curriculum.custids,
    (state) => state.curriculum.curriculumKey,
  ],
  (hideOptionalElements, setid, custids, curriculumKey) => {
    if (!hideOptionalElements) return false;
    const hideOptionalElementsForCur = hideOptionalElements.includes(
      setid || custids?.[0] || curriculumKey,
    );
    return hideOptionalElementsForCur;
  },
);

export const selectFullAnnotatedCurriculumViewModel = createTypedSelector(
  [
    // () => {
    //   console.time('selectFullAnnotatedCurriculumViewModelSelectors');
    //   return null;
    // },
    (state, params?: CurriculumParams) => getCurriculumKeyFromStateOrParams(state, params),
    (state, params?: CurriculumParams) =>
      getCurriculumKeyFromStateOrParams(state, params) !== 'nonderived'
        ? selectBaseCurriculumViewModel(state, params)
        : selectBaseNonDerivedCurriculumViewModel(state, params),
    selectAnnotatedCurriculumGoalListData,
    selectCustomCurriculaFromUrlOrParams,
    // (state) => state.curriculum.studids,
    selectCurrentSchoolyear,
    selectLayeredAnnotationsObjFromUrlOrParams,
    (state) => state.studyProgrammeApiData.allPrograms,
    selectLeidraadTop5AndEduPointers,
    (state, params?: CurriculumParams) => selectAllCreatorsActiveInSchoolyear(state, params),
    (state) =>
      state.userAndSchools.privateState?.state?.collapseSections?.[selectCurrentSchoolHref(state)],
    selectCanEditCustomCur,
    selectHideOptionalElements,
    (state) => state.distribute.distributionMode,
    // () => {
    //   console.timeEnd('selectFullAnnotatedCurriculumViewModelSelectors');
    //   return null;
    // },
  ],
  (
    // ignore,
    curriculumKey,
    baseCurrVM,
    extractedCurriculumNodes,
    customCurricula,
    currentSchoolyear,
    layeredAnnotationsObjResult,
    allPrograms,
    schoolChoices,
    allOrgs,
    collapseSections,
    canEditCustomCur,
    hideOptionalElements,
    isDistributionMode,
  ) => {
    if (!baseCurrVM) return baseCurrVM;
    if (!currentSchoolyear) return baseCurrVM;

    const endLogTime = conditionalLogTime('selectFullAnnotatedCurriculumViewModel', 2);

    const origViewModel = baseCurrVM;
    const { layeredAnnotationsObj } = layeredAnnotationsObjResult;

    let modifiedCurriculumNodes = extractedCurriculumNodes;
    const titles =
      layeredAnnotationsObj &&
      customCurricula &&
      customCurricula.length &&
      getTitleObjects(
        origViewModel.title,
        layeredAnnotationsObj[Object.keys(layeredAnnotationsObj)[0]],
        customCurricula.length,
        allPrograms,
        allOrgs,
        origViewModel.grade,
      );

    const { expired } = currentSchoolyear;
    const readonly = !canEditCustomCur;

    const collapsedSectionsForCur = collapseSections?.[curriculumKey];
    if (hideOptionalElements) {
      modifiedCurriculumNodes = triggerSelectedItems(modifiedCurriculumNodes);
    }
    modifiedCurriculumNodes = setCollapsedSections(
      modifiedCurriculumNodes,
      collapsedSectionsForCur,
    );

    if (isDistributionMode) {
      modifiedCurriculumNodes = modifyGoalsForDistribution(modifiedCurriculumNodes);
    }

    const viewModel = {
      ...origViewModel,
      schoolLevel: schoolChoices,
      ...titles,
      expired,
      readonly,
      doelenlijst: {
        ...buildGoalListTab(modifiedCurriculumNodes),
      },
    };

    endLogTime();
    // return deepFreeze(viewModel);
    return viewModel;
  },
  // {
  //   memoizeOptions: {
  //     // resultEqualityCheck: shallowEqual,
  //     resultEqualityCheck: deepEqualWithLog('selectFullAnnotatedCurriculumViewModel'),
  //     // resultEqualityCheck: shallowEqualWithLog('selectFullAnnotatedCurriculumViewModel'),
  //   },
  // }
);

export const selectParentTreeMap = createTypedSelector(
  [selectFullAnnotatedCurriculumViewModel],
  (vm) => {
    const vmmap = new Map();

    const mapChildren = (child, parent) => {
      const parentNode = new CurriculumNode(child, parent);
      vmmap.set(child.key, parentNode);

      child.goals?.forEach((g) => mapChildren(g, parentNode));
      child.sections?.forEach((s) => mapChildren(s, parentNode));
      child.subItems?.forEach((p) => mapChildren(p, parentNode));
    };

    mapChildren(vm.doelenlijst, null);
    return vmmap;
  },
);

export const selectIsGoalSelected = createTypedSelector(
  [(state) => state.curriculum.selectedItems, (_state, href: string) => href],
  (selectedGoals, goalHref) => {
    return selectedGoals?.find((g) => g.href === goalHref) ?? false;
  },
);

export const selectCurriculaReadOnly = createTypedSelector(
  [selectCanEditCustomCur, selectIsSchoolyearReadOnly],
  (canEdit, schoolyearReadOnly) => {
    return !canEdit || schoolyearReadOnly;
  },
);

export const selectCurriculumIdentifier = createTypedSelector(
  [selectFullAnnotatedCurriculumViewModel],
  (fullAnnotatedCurriculumViewModel) => {
    if (!fullAnnotatedCurriculumViewModel) return undefined;
    return fullAnnotatedCurriculumViewModel.identifiers?.[0];
  },
);

export const selectSelectedGoalsForPostmessage = (state: RootState) => {
  const { selectedItems } = state.curriculum;
  if (!selectedItems) return [];
  const allGoals = selectAllGoals(state);

  const selectedGoals = selectedItems.map((z) => allGoals[getKeyFromHref(z.href)] || z);

  return selectedGoals.map((i) => ({
    href: i.href,
    baseCurriculum: i.rootKey ? `/content/${i.rootKey}` : null,
  }));
};

/**
 * this selector expects the EditActivityModal to calculate and set the baseGoalOccurence into the state.
 * there is no way to repeatedly calculate the baseGoalOccurence without the EditActivityModal's state being in redux.
 * @param {*} state
 * @param {*} href
 * @returns
 */
export const selectGoalCount = (state: RootState, href: string) => {
  const { selectedItems, selectionMode, baseGoalOccurence } = state.curriculum;
  const goalOccurrences = baseGoalOccurence?.[href];
  if (!selectionMode || goalOccurrences === undefined) {
    return null;
  }
  const isItemSelected = selectedItems?.some((z) => z.href === href);
  const count = isItemSelected ? goalOccurrences + 1 : goalOccurrences;
  return count || null; // only return if count is not 0 (avoid react print 0)
};

export const selectCurriculumTabs = createTypedSelector(
  [
    (state) =>
      state.curriculum.curriculumKey !== 'nonderived'
        ? selectBaseCurriculumViewModel(state)
        : selectBaseNonDerivedCurriculumViewModel(state),
    (state) => Boolean(state.curriculum.custids?.length || state.curriculum.setid),
    (state) => state.distribute.distributionMode,
    (state) => state.curriculum.selectionMode,
  ],
  (baseCurriculum, isCustomCur, isDistributionMode, isSelectionMode) => {
    if (!baseCurriculum || isSelectionMode) return undefined;
    const curriculumTabs: any = [];

    let doelenlijstLabel = TABNAMES.doelenlijst;
    if (isCustomCur) {
      doelenlijstLabel = TABNAMES.customplan;
    }
    if (isDistributionMode) {
      doelenlijstLabel = TABNAMES.distributie;
    }

    Object.keys(CONTENTTYPES.tabs).forEach((key) => {
      if (
        (baseCurriculum[key] &&
          Object.keys(baseCurriculum[key]).length &&
          baseCurriculum[key].items?.length) || // check if the tab has content
        key === 'doelenlijst'
      ) {
        let routePart = `${key}`;
        if (key === 'doelenlijst' && isDistributionMode) {
          routePart = 'distributie'; // distribution has its own route, but it uses the doelenlijst button
        }
        curriculumTabs.push({
          key,
          label: key === 'doelenlijst' ? doelenlijstLabel : TABNAMES[key],
          route: routePart,
        });
      }
    });

    return curriculumTabs;
  },
);
